import React, { useState, useEffect } from "react";
import { MaterialReactTable, useMaterialReactTable } from 'material-react-table';
import Typography from "@mui/material/Typography";
import { Grid, IconButton, Box, Paper, Dialog, DialogTitle, DialogContent, DialogActions, Button, Stack, TextField, Modal, Tooltip, CircularProgress } from "@mui/material";
import { DataGrid} from "@mui/x-data-grid";
import { frFR, esES } from '@mui/x-data-grid/locales';
import useAxiosPrivate from "../utils/useAxiosPrivate";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from '@mui/icons-material/Cancel';
import DeleteIcon from '@mui/icons-material/Delete';
import SupervisorAccountIcon from '@mui/icons-material/SupervisorAccount';
import PersonIcon from '@mui/icons-material/Person';
import { useAuth } from "../utils/AuthProvider";
import { canManageUsers, canManageManagers } from "../utils/permissions";
import EditSettings from "../components/EditSettings";
import i18n from "../translationUtils/i18n";
import muiLocalizationsMap from "../translationUtils/muiLocalizations";

function CheckCell(params) {
  return (
    <Box sx={{ justifyContent: "center" }}>
      {params.value ? <CheckCircleIcon style={{ color: "green" }} /> : <CancelIcon style={{ color: "red" }} />}
    </Box>
  );
}

const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  minWidth: '40%',
  overflow: 'scroll',
  overflowX: "hidden",
  bgcolor: 'background.paper',
  border: '2px solid #000',
  boxShadow: 24,
  p: 4,
};

const EMAIL_REGEX = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|.(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

function CollapsibleRow(props) {
  const { cookies } = useAuth();
  const axiosPrivate = useAxiosPrivate();
  const { row } = props;

  const [rows, setRows] = useState([]);
  const [userStatus, setUserStatus] = useState([]);
  const [statusLoading, setStatusLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogTitle, setDialogTitle] = useState("");
  const [dialogText, setDialogText] = useState("");
  const [toDelete, setToDelete] = useState(null);
  const [promoteManager, setPromoteManager] = useState(null);
  const [demoteManager, setDemoteManager] = useState(null);

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const handleConfirmDialog = () => {
    if (toDelete) {
      const userToDelete = {
        userId: toDelete.id,
        csId: props.row.csId
      }

      axiosPrivate.post("/api/Site/deleteUser", userToDelete)
        .then(() => {
          fetchData();
        })
        .catch((error) => {
          console.error(error);
          alert("Error removing user. Please try again.");
        });
    }
    if (promoteManager) {
      const userToPromote = {
        userId: promoteManager.id,
        csId: props.row.csId
      }

      axiosPrivate.post("/api/Site/promoteUser", userToPromote)
        .then(() => {
          fetchData();
        })
        .catch((error) => {
          console.error(error);
          alert("Error promoting user. Please try again.");
        });
    }
    if (demoteManager) {
      const userToDemote = {
        userId: demoteManager.id,
        csId: props.row.csId
      }

      axiosPrivate.post("/api/Site/demoteUser", userToDemote)
        .then(() => {
          fetchData();
        })
        .catch((error) => {
          console.error(error);
          alert("Error demoting user. Please try again.");
        });
    }
    setToDelete(null);
    setPromoteManager(null);
    setDemoteManager(null);
    setOpenDialog(false);
    fetchData();
  };

  const managesThisSite = userStatus.some(
    u => u.userName === cookies.user && u.csId === props.row.csId && u.isManager
  );
  const canUsers = canManageUsers(cookies.role, managesThisSite);
  const canManagers = canManageManagers(cookies.role, managesThisSite);

  const columns = [
    { field: 'fullName', headerName: i18n.t('name'), width: 200 },
    { field: 'email', headerName: i18n.t('email'), width: 200 },
    { field: 'cellPhone', headerName: i18n.t('phone'), width: 150 },
    { field: 'smsNotification', headerName: i18n.t('textnotifications' ), width: 170, renderCell: CheckCell },
    { field: 'pushNotification', headerName: i18n.t('pushnotifications'), width: 150, renderCell: CheckCell },
    { field: 'emailNotification', headerName: i18n.t('emailnotifications'), width: 150, renderCell: CheckCell },
    {
      field: "action",
      headerName: i18n.t('actions'),
      width: 150,
      sortable: false,
      renderCell: (params) => {
        const handleDelete = (e) => {
          e.stopPropagation(); // don't select this row after clicking
          setDialogTitle(i18n.t('removeuserfromsite'));
          setDialogText(i18n.t('removeuserfromsitetip', {name: params.row.fullName, site: props.row.csName} ));
          setToDelete(params.row);
          setOpenDialog(true);
        };
        const handleManagerStatus = (e) => {
          e.stopPropagation(); // don't select this row after clicking
          if (userStatus.find(u => u.userId === params.row.id && u.csId === props.row.csId)?.isManager) {
            setDialogTitle(i18n.t('demotesitemanagertouser'));
            setDialogText(i18n.t('demotesitemanagertousertip', {name: params.row.fullName, site: props.row.csName} ));
            setDemoteManager(params.row);
          }
          else {
            setDialogTitle(i18n.t('promoteusertositemanager'));
            setDialogText(i18n.t('promoteusertositemanagertip', {name: params.row.fullName, site: props.row.csName} ));
            setPromoteManager(params.row);
          }
          setOpenDialog(true);
        };

        return (
          <Stack direction="row" alignItems="center">
            {statusLoading ? (
              <CircularProgress size={20} sx={{ ml: 1 }} />
            ) : userStatus.length > 0 && (
              <>
                {canManagers && (
                  <Tooltip title={userStatus.find(u => u.userId === params.row.id && u.csId === props.row.csId)?.isManager
                    ? i18n.t('demotemanagertouser') : i18n.t('promotetomanager')}>
                    <IconButton aria-label="promoteToManager" onClick={handleManagerStatus} color="primary">
                      {(userStatus.find(u => u.userId === params.row.id && u.csId === props.row.csId)?.isManager) ?
                        <SupervisorAccountIcon />
                        :
                        <PersonIcon />
                      }
                    </IconButton>
                  </Tooltip>
                )}
                {canUsers && (
                  <>
                    <Tooltip title={i18n.t('removeuser')}>
                      <IconButton aria-label="delete" onClick={handleDelete} color="primary">
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                    <EditSettings user={params.row.settings} />
                  </>
                )}
              </>
            )}
          </Stack>
        );
      }
    },
  ];

  const loadData = (users) => {
    if (users && users.length > 0) {
      let userRows = [];
      users.forEach((user) => {
        if (user['email'] !== cookies.user && !user.isDistributor) {
          userRows.push(
            {
              id: user['userId'],
              fullName: user['fullName'],
              email: user['email'],
              cellPhone: user['countryCode'] + ' ' + user['cellPhone'],
              smsNotification: user['sms'],
              pushNotification: user['pushNotification'],
              emailNotification: user['emailNotification'],
              settings: user
            }
          )
        }
      });

      setRows(userRows);
    }
  };

  const fetchData = () => {
    const controller = new AbortController();

    const getSites = async () => {
      try {
        const response = await axiosPrivate.get('/api/Site/siteUsers/' + row.csId, {
          signal: controller.signal
        });

        loadData(response.data);

      } catch (err) {
        alert(err);
      }
    }

    const getUserStatus = async () => {
      setStatusLoading(true);
      try {
        const response = await axiosPrivate.get('/api/Site/siteUserStatus/', {
          signal: controller.signal
        });

        setUserStatus(response.data);

      } catch (err) {
        alert(err);
      } finally {
        if (!controller.signal.aborted) setStatusLoading(false);
      }
    }

    getUserStatus();
    getSites();

    return () => {
      controller.abort();
    }
  }

  const localizationPicker = () => {
    if (i18n.locale === "fr-FR") {
      return frFR.components.MuiDataGrid.defaultProps.localeText
    } else if (i18n.locale === "es-ES") {
      return esES.components.MuiDataGrid.defaultProps.localeText
    } else {
      return null;
    }
  }

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line 
  }, []);

  return (
    <>
      <Paper sx={{ height: 400, minWidth: '100%' }}>
        <DataGrid
          rows={rows}
          columns={columns}
          sx={{ border: 0 }}
          localeText={localizationPicker()}
        />
      </Paper>
      <Dialog open={openDialog} onClose={handleCloseDialog}>
        <DialogTitle>{dialogTitle}</DialogTitle>
        <DialogContent>
          {dialogText}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} color="primary">
            {i18n.t('close')}
          </Button>
          <Button onClick={handleConfirmDialog} color="primary">
            {i18n.t('confirm')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export function Sites() {

  const { cookies } = useAuth();
  const axiosPrivate = useAxiosPrivate();

  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [userStatus, setUserStatus] = useState([]);

  const [inviteCs, setInviteCs] = useState(null);
  const [inviteEmail, setInviteEmail] = useState("");
  const [validEmail, setValidEmail] = useState(false);
  const [emailFocus, setEmailFocus] = useState(false);

  const handleOpen = (csId) => {
    setInviteCs(csId);
    setOpen(true);
  };
  const handleClose = () => {
    setInviteCs(null);
    setInviteEmail("");
    setOpen(false);
  };

  const handleSendInvite = () => {

    const customerContact = {
      csId: inviteCs,
      csContactEmail: inviteEmail
    }

    axiosPrivate.post("/api/User/sendEmailInvite", customerContact)
      .then(() => {
        alert(i18n.t('invitesent'));
        handleClose();
      })
      .catch((error) => {
        console.error(error);
        alert(i18n.t('invitefailed'));
      })
  };

  const fetchData = () => {
    const controller = new AbortController();

    const getSites = async () => {
      try {
        const response = await axiosPrivate.get('/api/Site/sitesAndSystems/' + cookies.user, {
          signal: controller.signal
        });

        loadData(response.data);

      } catch (err) {
        alert(err);
      }
    }

    const loadData = (sites) => {
      if (sites && sites.length > 0) {
        let siteRows = [];
        sites.forEach((site) => {
          siteRows.push(
            {
              csId: site.csId,
              csName: site.csName,
              csAddress1: site.csAddress1 + ", " + site.csCity,
              csAddress2: site.csAddress2,
              csState: site.csState,
              csAccountManager: site.csAccountManager,
              systems: site.units,
              centralStation: site.centralStation === "COPS" ? "Texas" : site.centralStation === "Acadian" ? "Louisiana" : site.centralStation,
            }
          )
        });
  
        setRows(siteRows);
        setLoading(false);
      }
    };

    const getUserStatus = async () => {
      try {
        const response = await axiosPrivate.get('/api/Site/siteUserStatus', {
          signal: controller.signal
        });
        
        setUserStatus(response.data);

      } catch (err) {
        alert(err);
      }
    }

    getUserStatus();
    getSites();

    return () => {
      controller.abort();
    }
  }

  const canInvite = (csId) => {
    const managesThisSite = userStatus.some(
      u => u.userName === cookies.user && u.csId === csId && u.isManager
    );
    return canManageUsers(cookies.role, managesThisSite);
  }

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line 
  }, []);

  useEffect(() => {
    setValidEmail(EMAIL_REGEX.test(String(inviteEmail).toLowerCase()));
  }, [inviteEmail]);

  const columns =
    [
      {
        accessorKey: 'csName',
        header: i18n.t('site'),
        enableColumnOrdering: false,
        minSize: 200,
      },
      {
        accessorKey: 'csAddress1',
        header: i18n.t('address'),
        minSize: 300,
      },
      {
        accessorKey: 'csState',
        header: i18n.t('state'),
        minSize: 30,
        maxSize: 60,
      },
      {
        accessorKey: 'csAccountManager',
        header: i18n.t('accountmanager'),
        enableColumnOrdering: false,
        minSize: 100,
      },
      {
        accessorKey: 'systems',
        header: i18n.t('systems'),
        enableColumnOrdering: false,
        minSize: 200,
      },
      {
        accessorKey: 'centralStation',
        header: i18n.t('centralstation'),
        size: 130,
      },
      {
        accessorKey: 'csId',
        header: '',
        size: 100,
        Cell: ({ row }) => (
          <Button
            variant="contained"
            disabled={!canInvite(row.original.csId)}
            onClick={() => { handleOpen(row.original.csId) }}
            sx={{ padding: "3px" }}>
            {i18n.t('inviteuser')}
          </Button>
        ),
      },
    ];

  const table = useMaterialReactTable({
    columns,
    data: rows,
    localization: muiLocalizationsMap,
    enableExpandAll: true,
    enableColumnOrdering: true,
    enableStickyHeader: true,
    enableColumnResizing: true,
    paginationDisplayMode: 'pages',
    layoutMode: 'grid',
    initialState: {
      columnPinning: { left: ['mrt-row-expand', 'csName'] },
      pagination: { pageSize: 100, pageIndex: 0 },
    },
    state: { 
      showSkeletons: loading,
    },
    muiPaginationProps: {
      shape: 'rounded',
      rowsPerPageOptions: [50, 100, 200],
      showFirstButton: true,
      showLastButton: true,
    },
    muiTableContainerProps: { sx: { maxHeight: '70vh' } },
    muiTableHeadCellProps: {
      sx: {
        '& .Mui-TableHeadCell-Content-Actions': {
          display: "none"
        },
        '&:hover .Mui-TableHeadCell-Content-Actions': {
          display: "block"
        },
        '& .Mui-TableHeadCell-Content-Labels span': {
          display: "none"
        },
        '&:hover .Mui-TableHeadCell-Content-Labels span': {
          display: "block"
        },
      },
    },
    //custom expand button rotation
    muiExpandButtonProps: ({ row, table }) => ({
      // onClick: () => table.setExpanded({ [row.id]: !row.getIsExpanded() }), //only 1 detail panel open at a time
      sx: {
        transform: row.getIsExpanded() ? 'rotate(180deg)' : 'rotate(-90deg)',
        transition: 'transform 0.2s',
      },
    }),

    //conditionally render detail panel
    renderDetailPanel: ({ row }) =>
      <CollapsibleRow key={row.original.id} row={row.original} />
  });

  return (
    <>
      <Grid
        container
        justifyContent="center" // Center vertically
        alignItems="center" // Center horizontally
        style={{ height: "2%", paddingTop: "1%", marginBottom: "1%" }} // Center vertically
      >
        <Typography
          variant="h6"
          alignItems="center"
          fontFamily="monospace">
          {i18n.t('sitesmanagement')}
        </Typography>
      </Grid>
      <MaterialReactTable table={table}/>
      <Modal
        open={open}
        onClose={handleClose}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box sx={style}>

          <Typography variant="h6" component="h2" style={{ paddingBottom: "20px" }}>
            {i18n.t('inviteuser')}
          </Typography>

          <TextField
            label={i18n.t('email')}
            fullWidth
            value={inviteEmail}
            onChange={(e) => setInviteEmail(e.target.value)}
            error={!validEmail && emailFocus && inviteEmail.length > 0}
            helperText={!validEmail && emailFocus && inviteEmail.length > 0 ? i18n.t('emailisinvalid') : ""}
            onFocus={() => setEmailFocus(true)}
            onBlur={() => setEmailFocus(false)}
            sx={{ marginBottom: 2 }}
          />

          <Grid item xs={12} style={{ paddingTop: "10px", paddingBottom: "10px" }}>
            <Box display="flex" justifyContent="flex-end">
              <Button variant="contained" onClick={handleSendInvite}>
                {i18n.t('send')}
              </Button>
            </Box>
          </Grid>

        </Box>
      </Modal>
    </>
  );
}